//*Import the React library
import React, { useState, useCallback, useEffect, useMemo } from "react";

//*Import hooks from react-router-dom
import { useNavigate } from "react-router-dom";

//*Import the List component
import { List } from "../../../component/list/list";
import { Loading } from "../../../component/common/Loading";

//*Import the useStudents hook
import { useStudents } from "../../../hooks/students/useStudents";

//*Import the AuthAPI service
import { AuthAPI } from "../../../services/apis/AuthAPI";

//*Import the UserRole enum
import { UserRole } from "../../../constants/enum/UserRole";

//* Import the CSVAdminManagementView component
import { CSVStudentManagementView } from "../../UsersStudentView/list/CSVManagementView";

interface Student {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  updatedAt: string;
}

interface User {
  role: UserRole;
}

var debounce = require("lodash.debounce");

export function UsersStudentView() {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();

  const [me, setMe] = useState<User | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showCSVStudentModal, setShowCSVStudentModal] = useState(false);
  const [totalStudents, setTotalStudents] = useState(0);

  const {
    students,
    loading,
    error,
    setSearch,
    pagination,
    setPagination,
    deleteStudent,
    refetch,
    changePage,
    isDeleting,
  } = useStudents(token);

  useEffect(() => {
    async function fetchMe() {
      try {
        const response = await AuthAPI.me(token);
        setMe(response.data);
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
    fetchMe();
  }, [token]);

  useEffect(() => {
    setTotalStudents(pagination.totalItems || 0);
  }, [pagination.totalItems]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (value.trim().length >= 3) {
          setSearch(value);
        } else {
          setSearch("");
          setPagination((prev) => ({ ...prev, currentPage: 1 }));
        }
        setIsSearching(false);
      }, 500),
    [setSearch, setPagination]
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (studentToDelete) {
      try {
        const currentPageItems = students.length;
        const currentPage = pagination.currentPage;

        await deleteStudent(studentToDelete);

        if (currentPageItems === 1 && currentPage > 1) {
          changePage(currentPage - 1);
        }

        setShowWarning(false);
        setStudentToDelete(null);
      } catch (error) {
        console.error("Error deleting Student:", error);
      }
    }
  }, [
    studentToDelete,
    deleteStudent,
    students.length,
    pagination.currentPage,
    changePage,
  ]);

  const handleDeleteActionClick = useCallback((id: string) => {
    setStudentToDelete(id);
    setShowWarning(true);
  }, []);

  const handleChangePagination = useCallback(
    (value: number) => {
      changePage(value);
    },
    [changePage]
  );

  const onImportClick = () => {
    setShowCSVStudentModal(true);
  };

  if (!me || isDeleting) {
    return <Loading />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="row">
      <List
        search_input_value={searchInputValue}
        hideManager
        search_text="Pesquise pelo nome ou email do aluno"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={refetch}
        additionalButtons={
          me.role === UserRole.TEACHER_USER
            ? []
            : [
                {
                  label: "Importar Alunos",
                  eventHandler: onImportClick,
                },
              ]
        }
        title={`Alunos (Total: ${totalStudents})`}
        not_found_message="Nenhum aluno encontrado"
        button_link="/users-student/create"
        button_text="Adicionar aluno"
        deleting_message="Ao deletar este aluno, ele não estará mais disponível no sistema"
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onEditActionClick={(id) => navigate(`/users-student/edit/${id}`)}
        onChangePagination={handleChangePagination}
        show_warning={showWarning}
        loading_screen={loading || isSearching}
        items={students.map((Student: Student) => ({
          ...Student,
          key: Student.id,
        }))}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          { name: "name", type: "text", header: "Nome" },
          { name: "email", type: "text", header: "Email" },
        ]}
        loading_search={isSearching}
      />
      {showCSVStudentModal && (
        <CSVStudentManagementView
          token={token}
          onClose={() => setShowCSVStudentModal(false)}
          refetch={refetch}
        />
      )}
    </div>
  );
}
